import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../feedback/ToastProvider';

const COOLDOWN_SEC = 30;

/**
 * "Call waiter" action: notifies the waiter + admin and enforces a 30-second
 * cooldown so the button can't be spammed. The cooldown is keyed per table in
 * localStorage, so it persists across the menu / tracking screens.
 *
 * Pass `notify` (→ the Web API's `POST /api/order/call-waiter`) to actually notify
 * staff; the toast + cooldown are the local UX around it.
 */
export function useCallWaiter(tableCode = 'default', notify?: () => Promise<void> | void) {
  const toast = useToast();
  const key = `thalios.waiterCall.${tableCode}`;

  const compute = () => {
    const last = Number(window.localStorage.getItem(key) || 0);
    return Math.max(0, COOLDOWN_SEC - Math.floor((Date.now() - last) / 1000));
  };

  const [cooldown, setCooldown] = useState(compute);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => setCooldown(compute), 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooldown]);

  const call = useCallback(() => {
    if (cooldown > 0) return;
    window.localStorage.setItem(key, String(Date.now()));
    setCooldown(COOLDOWN_SEC);
    Promise.resolve(notify?.()).catch(() => {
      /* best-effort — the cooldown/toast still stand */
    });
    toast.show('Waiter notified — someone is on the way', 'success');
  }, [cooldown, key, toast, notify]);

  return { call, cooldown, disabled: cooldown > 0 };
}
